import React, { useState, useRef, useCallback } from 'react';
import { Card, Table, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { adminWarehouseApi } from 'api';

const ChannelSettingsSection = ({
  warehouseId,
  channelSettings,
  refetch,
  // 전달 받은 도메인(권장): 부모(WarehouseDetail)에서 상세 응답의 domain을 넘겨줌
  warehouseDomain,
}) => {
  const [updating, setUpdating] = useState({});
  // Row-level lock: when any toggle in a row is being updated, lock all three
  const [rowUpdating, setRowUpdating] = useState({});
  const debounceTimers = useRef({});

  const handleToggleChange = useCallback(
    async (channelSetting, settingKey, newValue) => {
      // Backend endpoint expects channel setting id
      const channelSettingId = channelSetting?.channel?.id;

      if (!channelSettingId) {
        console.warn('Channel setting id is missing in item:', channelSetting);
        toast.error('잘못된 채널 설정 항목입니다. (id 누락)');
        return;
      }

      // 1) 우선 부모로부터 전달받은 warehouseDomain 사용 (예외 없이 가장 안전)
      const domain = (warehouseDomain || '').trim();
      if (!domain) {
        // 더 이상 _getWarehouseDomainStrict으로 예외를 던지지 않도록, 사용자에게 안내
        toast.error(
          'Warehouse domain is not available. 창고를 다시 선택하거나 새로고침 후 시도해 주세요.',
        );
        return;
      }
      const updateKey = `${channelSettingId}-${settingKey}`;
      const rowKey = `${channelSettingId}`;

      // Clear existing debounce timer for this specific setting
      if (debounceTimers.current[updateKey]) {
        clearTimeout(debounceTimers.current[updateKey]);
      }

      // Mark this specific toggle and entire row as updating
      setUpdating(prev => ({ ...prev, [updateKey]: true }));
      setRowUpdating(prev => ({ ...prev, [rowKey]: true }));

      // Debounce the API call (300ms delay)
      debounceTimers.current[updateKey] = setTimeout(async () => {
        try {
          // Call API to update channel setting
          await adminWarehouseApi.updateChannelSetting(
            domain,
            channelSettingId,
            {
              [settingKey]: newValue,
            },
          );

          // Show success message
          toast.success('Channel settings updated successfully');

          // Refetch warehouse data to get updated channel settings
          await refetch();
        } catch (error) {
          console.error('Failed to update channel setting:', error);

          // Try to extract server-side validation messages (400) like
          // { "inventory_sync": ["At least one of inventory_sync must be True for this channel."] }
          const extractFirstErrorMessage = data => {
            try {
              if (!data || typeof data !== 'object') return null;
              // Common non-field keys
              if (typeof data.detail === 'string') return data.detail;
              if (typeof data.message === 'string') return data.message;

              // Field errors: find the first array of strings and return its first item
              for (const key of Object.keys(data)) {
                const val = data[key];
                if (Array.isArray(val) && val.length > 0) {
                  const first = val[0];
                  if (typeof first === 'string') return first;
                }
                // Some backends return string directly per field
                if (typeof val === 'string') return val;
              }
            } catch (err) {
              // Ensure catch block is not empty for eslint(no-empty)
              // In case of parsing issues, just return null and fall back to other messages
              return null;
            }
            return null;
          };

          const serverData = error?.response?.data;
          const fieldMsg = extractFirstErrorMessage(serverData);
          const fallbackMsg = error?.message;

          // Show the most specific message available
          if (fieldMsg) {
            toast.error(fieldMsg);
          } else if (serverData?.detail || serverData?.message) {
            toast.error(serverData.detail || serverData.message);
          } else if (fallbackMsg) {
            toast.error(`Failed to update channel settings: ${fallbackMsg}`);
          } else {
            toast.error('Failed to update channel settings');
          }
          // 실패 시에도 서버 상태로 동기화하여 UI를 되돌립니다.
          try {
            await refetch();
          } catch (e) {
            // ignore refetch error here; toast already shown
          }
        } finally {
          // Remove updating state
          setUpdating(prev => {
            const newState = { ...prev };
            delete newState[updateKey];
            return newState;
          });
          setRowUpdating(prev => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
          });
          // Clean up timer reference
          delete debounceTimers.current[updateKey];
        }
      }, 300);
    },
    [refetch, warehouseDomain],
  );

  if (!channelSettings || channelSettings.length === 0) {
    return null;
  }

  const domainAvailable = Boolean((warehouseDomain || '').trim());

  return (
    <Card className="mt-3">
      <Card.Header className="bg-light">
        <h5 className="mb-0">Channel Settings</h5>
      </Card.Header>
      <Card.Body>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Channel ID</th>
              <th>Inventory Sync</th>
              <th>Order Create Sync</th>
              <th>Order Cancel Sync</th>
            </tr>
          </thead>
          <tbody>
            {channelSettings.map(setting => {
              const csId = setting?.id;
              const inventoryUpdateKey = `${csId}-inventory_sync`;
              const orderCreateUpdateKey = `${csId}-order_create_sync`;
              const orderCancelUpdateKey = `${csId}-order_cancel_sync`;

              return (
                <tr key={setting.id}>
                  <td>
                    Channel {setting?.channel?.id ?? setting?.channel_id ?? '-'}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id={`channel-inventory-${csId}`}
                        checked={setting.inventory_sync || false}
                        onChange={e =>
                          handleToggleChange(
                            setting,
                            'inventory_sync',
                            e.target.checked,
                          )
                        }
                        disabled={rowUpdating[csId] || !domainAvailable}
                      />
                      {updating[inventoryUpdateKey] && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id={`channel-order-create-${csId}`}
                        checked={setting.order_create_sync || false}
                        onChange={e =>
                          handleToggleChange(
                            setting,
                            'order_create_sync',
                            e.target.checked,
                          )
                        }
                        disabled={rowUpdating[csId] || !domainAvailable}
                      />
                      {updating[orderCreateUpdateKey] && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id={`channel-order-cancel-${csId}`}
                        checked={setting.order_cancel_sync || false}
                        onChange={e =>
                          handleToggleChange(
                            setting,
                            'order_cancel_sync',
                            e.target.checked,
                          )
                        }
                        disabled={rowUpdating[csId] || !domainAvailable}
                      />
                      {updating[orderCancelUpdateKey] && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default ChannelSettingsSection;
